import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { useMemo } from 'react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { PlanSummaryCard, BenefitSectionAccordion } from '@/components/insurance/coverage';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Eye } from 'lucide-react';
import type { IBenefitSectionData, IUtilizationData } from '@/@types/coverage';

function PreviewSkeleton() {
  return (
    <div className="min-h-full bg-background-secondary">
      <div className="bg-card border-b border-border px-8 py-5">
        <Skeleton className="h-8 w-48" />
      </div>
      <div className="px-8 py-6 space-y-6">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    </div>
  );
}

export function BenefitPlanPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTab = searchParams.get('tab') || 'plan-summary';

  const planId = id as Id<'benefitPlans'>; // AS_JUSTIFIED: one-time Id cast from route params

  const plan = useQuery(api.insurance.benefitPlans.getForPreview, planId ? { planId } : 'skip');

  // Empty utilization — preview shows a fresh policy with zero usage
  const emptyUtilization = useMemo(() => new Map<string, IUtilizationData>(), []);

  if (plan === undefined) {
    return <PreviewSkeleton />;
  }

  if (plan === null) {
    return (
      <div className="min-h-full bg-background-secondary flex flex-col items-center justify-center py-20">
        <p className="text-sm text-muted-foreground">Plan not found.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/insurance/benefit-plans')}
        >
          Back to Benefit Plans
        </Button>
      </div>
    );
  }

  const sections: IBenefitSectionData[] = plan.sections.map(s => ({
    _id: s._id,
    name: s.name,
    sortOrder: s.sortOrder,
    items: s.items,
  }));

  return (
    <div className="min-h-full bg-background-secondary">
      {/* Preview banner */}
      <div className="bg-blue-500/10 dark:bg-blue-500/20 border-b border-blue-500/30 px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="size-4 text-blue-500" />
          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
            Preview Mode — This is how customers will see this plan
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/insurance/benefit-plans/${id}?tab=${returnTab}`)}
        >
          <ArrowLeft className="size-4 mr-1.5" />
          Back to Plan
        </Button>
      </div>

      {/* Coverage header */}
      <div className="bg-card border-b border-border px-8 py-5">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-foreground">Coverage Details</h1>
          <span className="text-sm text-muted-foreground">Plan: {plan.name}</span>
        </div>
      </div>

      {/* Coverage content — identical to customer view */}
      <div className="px-8 py-6 space-y-6">
        <PlanSummaryCard
          planName={plan.name}
          status={plan.status}
          maxAnnualBenefit={plan.maxAnnualBenefit}
          annualDeductible={plan.annualDeductible}
          coinsurancePlanPays={plan.coinsurancePlanPays}
          coinsuranceYouPay={plan.coinsuranceYouPay}
          areaOfCoverage={plan.areaOfCoverage}
          currency={plan.currency}
        />

        <h2 className="text-lg font-semibold text-foreground">Coverage Items</h2>

        <BenefitSectionAccordion
          sections={sections}
          utilizationByItemId={emptyUtilization}
          currency={plan.currency}
          planCoinsurance={plan.coinsurancePlanPays}
        />
      </div>
    </div>
  );
}
